<template>
  <div
    :class="[
      'home-cash-actions',
      { 'home-cash-actions--loaded': addLoadedClass }
    ]"
  >
    <HomeHeroActionsLoader v-if="props.isLoading" />
    <div v-else class="home-cash-actions__list">
      <div
        v-for="(action, index) in buttons"
        :key="index"
        ref="setActionItemRef"
        :class="listItemClasses(action.disabled ?? false)"
      >
        <ButtonBoxLaunch
          :class="action.class"
          :disabled="props.disableActions || action.disabled"
          :label="action.label"
          :loading="props.isLoading"
          :icon="action.icon"
          :is-blur="true"
          :data-testid="`cash-action-${action.label.toLowerCase()}`"
          :type="action.primary ? 'primary' : 'secondary'"
          @click="action.handler"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, onMounted, nextTick, computed } from 'vue';
import { useRouter } from 'vue-router/composables';
import { UIModule } from '@/modules/common/store/UIModule';

import { ButtonBoxLaunch } from '@warrenbrasil/nebraska-web';
import {
  trackInvestClick,
  trackDepositClick,
  trackTransferClick
} from '../track-events';

import {
  ButtonAction,
  HomeBalanceHero
} from '@/modules/home/services/balances';

import type { Action } from './types';
import HomeHeroActionsLoader from './HomeHeroActionsLoader.vue';

const ITEM_TRANSITION_BASE_DELAY = 400;

const props = defineProps<{
  isLoading?: boolean;
  disableActions?: boolean;
  isOverdue: boolean;
  heroButtons: HomeBalanceHero;
}>();

const addLoadedClass = computed(() => !UIModule.getIsOpenWelcomeCurtain);

const router = useRouter();
const actionItemsRefs = ref<HTMLElement[]>([]);

function setActionItemRef(el: HTMLElement | null) {
  if (el) {
    actionItemsRefs.value.push(el);
  }
}

const buttons = ref<Action[]>([]);

onMounted(async () => {
  await nextTick();
  addAnimationDelayOnButtons();
});

watch(
  () => props.heroButtons,
  newVal => {
    if (newVal?.buttons?.length) {
      buttons.value = setButtons(newVal);
    }
  },
  { immediate: true, deep: true }
);

function setButtons(heroButtons: HomeBalanceHero): Action[] {
  return heroButtons.buttons.map(({ label, icon, isActive, action }) => {
    return {
      label,
      icon,
      disabled: !isActive,
      ...getHandlers(action)
    };
  });
}

function getHandlers(action: ButtonAction) {
  return {
    [ButtonAction.Invest]: {
      handler: goToInvestmentFlow,
      class: 'home-cash-actions__to_invest_container'
    },
    [ButtonAction.Deposit]: {
      handler: depositAction
    },
    [ButtonAction.Transfer]: {
      handler: handleTransferClick
    }
  }[action];
}

function goToInvestmentFlow() {
  if (props.isOverdue) {
    regulatoryUpdate();
    return;
  }

  router.push({ name: 'investment' });
  trackInvestClick();
}

function depositAction() {
  router.push({ name: 'deposit-pix' });
  trackDepositClick();
}

function handleTransferClick() {
  trackTransferClick();
  goToNewTransferPage();
}

function goToNewTransferPage() {
  router.push({ name: 'new-transfer' });
}

function regulatoryUpdate() {
  router.push({ name: 'regulatoryUpdateAnnouncement' });
}

function listItemClasses(isDisabled: boolean) {
  return [
    'home-cash-actions__item',
    isDisabled && 'home-cash-actions__item--disabled'
  ];
}

function addAnimationDelayOnButtons() {
  actionItemsRefs.value.forEach((item, index) => {
    const delay = ITEM_TRANSITION_BASE_DELAY + index * 60;
    item.style.transitionDelay = `${delay}ms`;
  });
}
</script>

<style lang="less" scoped>
@buttons-transition-timing-function: cubic-bezier(0.42, 0, 0.04, 1);

.home-cash-actions {
  &__to_invest_container {
    width: 320px;
  }

  &__list {
    display: flex;
    width: 100%;
  }

  &:not(&--loaded) &__item {
    opacity: 0;
    transform: translateY(180px);
  }

  &--loaded &__item {
    transition: opacity, transform 1s @buttons-transition-timing-function;
    opacity: 1;
    transform: translateY(0px);
  }

  &__item {
    margin-right: @size-spacing-x300;

    &--disabled {
      cursor: not-allowed;
    }

    &:last-of-type {
      margin-right: 0;
    }
  }

  .to-md({
    width: 100%;

    &__list {
      width: 100%;
      box-sizing: content-box;
      padding: @size-spacing-x150 @size-spacing-x400 @size-spacing-x350 0;
      margin: calc(@size-spacing-x150 - 1) 0 calc(@size-spacing-x350 - 1);
      overflow: auto;
    }
  });
}
</style>
